import { useContext } from "react";
import { AdminProjectContext } from "../../../contexts/AdminProjectContext";
import styled from "styled-components";
import {
    Warning,
    AttachMoney,
    AccountBalance,
    TrendingUp,
    PieChart,
    Event,
    Flag,
    Schedule,
    AccessTime,
    Info
} from "@mui/icons-material";
import { Card, CardContent } from "@mui/material";
import { StyledTooltip } from "../../ui/StyledTooltip";

// Styled Components - Compact version
const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;

  .MuiSvgIcon-root {
    color: var(--text-primary);
    font-size: 0.9rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
`;

const DetailCard = styled(Card)<{ variant?: 'primary' | 'success' | 'warning' | 'info' }>`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
  }

  /* &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => {
      switch (props.variant) {
        case 'success': return 'linear-gradient(180deg, #43e97b, #38f9d7)';
        case 'warning': return 'linear-gradient(180deg, #f093fb, #f5576c)';
        case 'info': return 'linear-gradient(180deg, #4facfe, #00f2fe)';
        default: return 'linear-gradient(180deg, var(--primary-color), var(--secondary-color))';
      }
    }};
    border-radius: 12px 0 0 12px;
  } */
`;

const DetailCardContent = styled(CardContent)`
  padding: 0.875rem 1rem !important;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DetailIconContainer = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .MuiSvgIcon-root {
    color: var(--text-primary);
    font-size: 1.1rem;
  }
`;

const DetailValue = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const DetailAmount = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailLabel = styled.p`
  font-size: 0.7rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;


export function AdminActiveProjectDetailsView() {
    const { selectedProject } = useContext(AdminProjectContext);

    if (!selectedProject) {
        return null;
    }
    function formatDay(day: number | null | undefined) {
        return (typeof day === "number") ? day.toString().padStart(2, "0") : "N/A";
    }
    function getDueDate(dayOfMonth: number) {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth(); // 0 = janvier
        return new Date(year, month, dayOfMonth);
    }
    const dueDate = getDueDate(selectedProject.dueDay ?? 5);

    // Date de fin : si pas de participants ou date invalide, afficher date de début
    function getDisplayEndDate() {
        const endDate = selectedProject?.endDate;
        const startDate = selectedProject?.startDate;
        const hasParticipants = selectedProject?.Users && selectedProject.Users.length > 0;
        const isValidEndDate = endDate && new Date(endDate).getFullYear() > 1970;

        if (!hasParticipants || !isValidEndDate) {
            return startDate ? new Date(startDate).toLocaleDateString("fr-FR") : "N/A";
        }
        return new Date(endDate).toLocaleDateString("fr-FR");
    }






    const scheduleDetails = [
        {
            id: 6,
            icon: <Event />,
            value: new Date(selectedProject.startDate).toLocaleDateString("fr-FR") ?? "N/A",
            label: "Date de début",
            variant: 'primary' as const,
            iconGradient: "linear-gradient(45deg, #667eea, #764ba2)",
            tooltip: "Début du cycle de cotisations"
        },
        {
            id: 7,
            icon: <Flag />,
            value: getDisplayEndDate(),
            label: "Date de fin",
            variant: 'warning' as const,
            iconGradient: "linear-gradient(45deg, #f093fb, #f5576c)",
            tooltip: "Clôture prévue du projet"
        },
        {
            id: 8,
            icon: <Schedule />,
            value: `${formatDay(selectedProject.dueDay)} du mois`,
            label: "Jour d'échéance",
            variant: 'success' as const,
            iconGradient: "linear-gradient(45deg, #43e97b, #38f9d7)",
            tooltip: "Date limite de paiement mensuel"
        },
        {
            id: 9,
            icon: <AccessTime />,
            value: `${formatDay(selectedProject.withdrawalDay)} du mois`,
            label: "Jour de retrait",
            variant: 'info' as const,
            iconGradient: "linear-gradient(45deg, #4facfe, #00f2fe)",
            tooltip: "Versement au bénéficiaire"
        }
    ];


    const DetailCardComponent = ({ detail }: { detail: any }) => (
        <StyledTooltip title={detail.tooltip || ''} placement="top">
            <DetailCard variant={detail.variant}>
                <DetailCardContent>
                    <DetailHeader>
                        <DetailIconContainer>
                            {detail.icon}
                        </DetailIconContainer>
                        <DetailValue>
                            <DetailLabel>{detail.label}</DetailLabel>
                            <DetailAmount>{detail.value}</DetailAmount>
                        </DetailValue>
                    </DetailHeader>
                </DetailCardContent>
            </DetailCard>
        </StyledTooltip>
    );

    return (
        <DetailsContainer>
            {/* Financial Details Section */}
            {/*<div>*/}
            {/*    <SectionHeader>*/}
            {/*        <SectionIcon>*/}
            {/*            <AttachMoney />*/}
            {/*        </SectionIcon>*/}
            {/*        <SectionTitle>Détails financiers</SectionTitle>*/}
            {/*    </SectionHeader>*/}
            {/*    <DetailsGrid>*/}
            {/*        {financialDetails.map(detail => (*/}
            {/*            <DetailCardComponent key={detail.id} detail={detail} />*/}
            {/*        ))}*/}
            {/*    </DetailsGrid>*/}
            {/*</div>*/}

            {/* Schedule Details Section */}
            <div>
                <SectionHeader>
                    <SectionIcon>
                        <Event />
                    </SectionIcon>
                    <SectionTitle>Planning et échéances</SectionTitle>
                </SectionHeader>
                <DetailsGrid>
                    {scheduleDetails.map(detail => (
                        <DetailCardComponent key={detail.id} detail={detail} />
                    ))}
                </DetailsGrid>
            </div>
        </DetailsContainer>
    );
}